(() => {
  const runtimeKey = "__jscaCinematicHeroRuntime";

  if (window[runtimeKey]) {
    window[runtimeKey].scan(document);
    return;
  }

  class CinematicHero {
    constructor(root) {
      this.root = root;
      this.media = root.querySelector("[data-hero-media]");
      this.video = root.querySelector("[data-jsca-autoplay-video]");
      this.abortController = new AbortController();
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.finePointer = window.matchMedia("(pointer: fine)");
      this.frame = 0;
      this.mouseX = 0;
      this.mouseY = 0;
      this.initVideo();
      this.initMotion();
    }

    on(target, type, listener, options = {}) {
      if (!target) return;
      target.addEventListener(type, listener, {
        ...options,
        signal: this.abortController.signal,
      });
    }

    initVideo() {
      if (!this.video) return;

      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.controls = false;
      this.video.disablePictureInPicture = true;
      this.video.disableRemotePlayback = true;
      this.video.setAttribute("muted", "");
      this.video.setAttribute("loop", "");
      this.video.setAttribute("playsinline", "");
      this.video.setAttribute("webkit-playsinline", "");
      this.video.setAttribute("controlslist", "nodownload nofullscreen noremoteplayback");
      this.video.setAttribute("disablepictureinpicture", "");
      this.video.removeAttribute("controls");

      const playVideo = () => {
        if (
          this.reducedMotion.matches ||
          document.visibilityState === "hidden" ||
          this.video.readyState < 2
        ) {
          return;
        }
        const attempt = this.video.play();
        if (attempt && typeof attempt.catch === "function") attempt.catch(() => {});
      };

      const syncPlaybackPreference = () => {
        if (this.reducedMotion.matches) {
          this.video.autoplay = false;
          this.video.removeAttribute("autoplay");
          this.video.pause();
          return;
        }
        this.video.autoplay = true;
        this.video.setAttribute("autoplay", "");
        playVideo();
      };

      this.on(this.video, "loadeddata", playVideo, { once: true });
      this.on(this.video, "canplay", playVideo, { once: true });
      this.on(window, "pageshow", playVideo);
      this.on(document, "visibilitychange", playVideo);
      this.on(this.reducedMotion, "change", syncPlaybackPreference);
      ["pointerdown", "touchstart", "click"].forEach((eventName) => {
        this.on(document, eventName, playVideo, { once: true, passive: true });
      });
      syncPlaybackPreference();
    }

    initMotion() {
      const requestRender = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => this.render());
      };

      this.on(window, "scroll", requestRender, { passive: true });
      this.on(window, "resize", requestRender, { passive: true });
      this.on(this.reducedMotion, "change", requestRender);
      this.on(window, "pointermove", (event) => {
        if (event.pointerType === "touch" || !this.finePointer.matches || this.reducedMotion.matches) return;
        this.mouseX = (event.clientX / window.innerWidth - 0.5) * 2;
        this.mouseY = (event.clientY / window.innerHeight - 0.5) * 2;
        requestRender();
      }, { passive: true });

      this.render();
    }

    render() {
      this.frame = 0;

      if (this.reducedMotion.matches) {
        this.root.style.setProperty("--scroll-progress", "0");
        this.root.style.setProperty("--mouse-x", "0");
        this.root.style.setProperty("--mouse-y", "0");
        this.root.style.setProperty("--parallax-x", "50%");
        this.root.style.setProperty("--parallax-y", "50%");
        this.media?.style.removeProperty("transform");
        return;
      }

      const bounds = this.root.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, -bounds.top / Math.max(1, window.innerHeight)));
      this.root.style.setProperty("--scroll-progress", String(progress));
      this.root.style.setProperty("--mouse-x", this.mouseX.toFixed(4));
      this.root.style.setProperty("--mouse-y", this.mouseY.toFixed(4));
      this.root.style.setProperty("--parallax-x", `${50 + this.mouseX * 1.2}%`);
      this.root.style.setProperty("--parallax-y", `${50 + this.mouseY * 1.2}%`);

      if (this.media) {
        const scale = 1 + progress * 0.032;
        this.media.style.transform = `scale(${scale}) translate3d(${this.mouseX * -5}px, ${progress * 20 + this.mouseY * -4}px, 0)`;
      }
    }

    destroy() {
      this.abortController.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.video?.pause();
      this.media?.style.removeProperty("transform");
    }
  }

  const controllers = new WeakMap();

  const rootsWithin = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches("[data-jsca-hero]")) roots.push(scope);
    scope.querySelectorAll?.("[data-jsca-hero]").forEach((root) => roots.push(root));
    return roots;
  };

  const scan = (scope = document) => {
    rootsWithin(scope).forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.set(root, new CinematicHero(root));
    });
  };

  window[runtimeKey] = { scan };
  document.addEventListener("shopify:section:load", (event) => scan(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    rootsWithin(event.target).forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan(document), { once: true });
  } else {
    scan(document);
  }
})();
